import { Label } from "@/ui/primitives/label";
import Button from "@imminently/decisively-ui/Button";
import DeciDateTimeField from "@imminently/decisively-ui/DateTimeField";
import DeciDateTimeInput from "@imminently/decisively-ui/DateTimeInput";
import DeciDateTimePicker from "@imminently/decisively-ui/DateTimePicker";
import Form from "@imminently/decisively-ui/Form";
import TextInput from "@imminently/decisively-ui/TextInput";
import DeciTimeField from "@imminently/decisively-ui/TimeField";
import DeciTimeInput from "@imminently/decisively-ui/TimeInput";
import { format } from "date-fns";
import * as React from "react";
import { ComponentPage, ComponentPreview, ComponentSection } from "../components/ComponentPage";

const formatValue = (value: Date | null) => value?.toISOString() ?? "null";
const formatDateOnlyValue = (value: Date | null) => (value ? format(value, "yyyy-MM-dd") : "null");
const dateStringSerialize = {
  to: (value: Date | null) => (value ? format(value, "yyyy-MM-dd") : ""),
  from: (value: string | null | undefined) => (value ? new Date(`${value}T00:00:00`) : null),
};

export function DatePickerPage() {
  const [interviewDateValue, setInterviewDateValue] = React.useState("now");
  const [nativeDateValue, setNativeDateValue] = React.useState("2026-05-04");
  const [deciDateValue, setDeciDateValue] = React.useState<Date | null>(new Date());
  const [deciDateTimeValue, setDeciDateTimeValue] = React.useState<Date | null>(new Date());
  const [serializedDateValue, setSerializedDateValue] = React.useState("2026-05-05");
  const [deciTimeValue, setDeciTimeValue] = React.useState("09:30");
  const [deciNowTimeValue, setDeciNowTimeValue] = React.useState("now");
  const [deciNowDateValue, setDeciNowDateValue] = React.useState<Date | null>(null);
  const [deciNowSelected, setDeciNowSelected] = React.useState(true);
  const [popoverRootValue, setPopoverRootValue] = React.useState<Date | null>(new Date());
  const [popoverContentValue, setPopoverContentValue] = React.useState<Date | null>(new Date());

  return (
    <ComponentPage
      title="Date Picker"
      description="A showcase of the date and time picker patterns currently present in the portal."
    >
      <ComponentSection
        title="Deci UI Inputs"
        description="New deci UI controlled primitives based on the shadcn Popover + Calendar composition."
      >
        <ComponentPreview className="items-stretch justify-start">
          <div className="grid w-full max-w-xl gap-4">
            <div className="grid gap-1.5">
              <Label>Deci DateTimeInput date object</Label>
              <DeciDateTimeInput
                dateOnly
                serialize={false}
                value={deciDateValue}
                onValueChange={setDeciDateValue}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">{formatDateOnlyValue(deciDateValue)}</code>
            </div>
            <div className="grid gap-1.5">
              <Label>Deci DateTimeInput clearable</Label>
              <DeciDateTimeInput
                dateOnly
                serialize={false}
                value={deciDateValue}
                onValueChange={setDeciDateValue}
                clearable
                clearAriaLabel="Clear date"
                startAdornment={<span className="text-muted-foreground">Date</span>}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">{formatDateOnlyValue(deciDateValue)}</code>
            </div>
            <div className="grid gap-1.5">
              <Label>Deci DateTimeInput allowNow</Label>
              <DeciDateTimeInput
                dateOnly
                allowNow
                allowArbitrary
                serialize={false}
                now={deciNowSelected}
                onNowChange={setDeciNowSelected}
                value={deciNowDateValue}
                onValueChange={setDeciNowDateValue}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">
                {deciNowSelected ? "now" : formatDateOnlyValue(deciNowDateValue)}
              </code>
            </div>
            <div className="grid gap-1.5">
              <Label>Deci DateTimeInput default string</Label>
              <DeciDateTimeInput
                dateOnly
                value={serializedDateValue}
                onValueChange={setSerializedDateValue}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">{serializedDateValue}</code>
            </div>
            <div className="grid gap-1.5">
              <Label>Deci DateTimeInput</Label>
              <DeciDateTimeInput
                serialize={false}
                value={deciDateTimeValue}
                onValueChange={setDeciDateTimeValue}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">{formatValue(deciDateTimeValue)}</code>
            </div>
            <div className="grid gap-1.5">
              <Label>Deci TimeInput</Label>
              <DeciTimeInput
                value={deciTimeValue}
                onValueChange={setDeciTimeValue}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">{deciTimeValue}</code>
            </div>
            <div className="grid gap-1.5">
              <Label>Deci TimeInput allowNow</Label>
              <DeciTimeInput
                allowNow
                value={deciNowTimeValue}
                onValueChange={setDeciNowTimeValue}
                fullWidth
              />
              <code className="text-xs text-muted-foreground">{deciNowTimeValue || "empty"}</code>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Deci UI DateTimeInput Popover"
        description="Compound popover parts for custom trigger and content composition."
      >
        <ComponentPreview className="items-stretch justify-start">
          <div className="grid w-full max-w-xl gap-4">
            <div className="grid gap-1.5">
              <Label>Root-powered popover</Label>
              <DeciDateTimePicker
                dateOnly
                value={popoverRootValue}
                onValueChange={setPopoverRootValue}
              >
                <DeciDateTimePicker.Reference asChild>
                  <Button label="Choose date">
                    {popoverRootValue ? popoverRootValue.toISOString().slice(0, 10) : "Choose date"}
                  </Button>
                </DeciDateTimePicker.Reference>
                <DeciDateTimePicker.Content />
              </DeciDateTimePicker>
              <code className="text-xs text-muted-foreground">{formatValue(popoverRootValue)}</code>
            </div>

            <div className="grid gap-1.5">
              <Label>Content-controlled popover</Label>
              <DeciDateTimePicker>
                <DeciDateTimePicker.Reference asChild>
                  <Button
                    variant="outline"
                    label="Choose date and time"
                  >
                    {popoverContentValue
                      ? popoverContentValue.toISOString().slice(0, 16).replace("T", " ")
                      : "Choose date and time"}
                  </Button>
                </DeciDateTimePicker.Reference>
                <DeciDateTimePicker.Content
                  value={popoverContentValue}
                  onValueChange={setPopoverContentValue}
                />
              </DeciDateTimePicker>
              <code className="text-xs text-muted-foreground">{formatValue(popoverContentValue)}</code>
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Deci UI Width"
        description="Date and time inputs follow the same intrinsic-by-default sizing as text inputs."
      >
        <ComponentPreview className="flex-col items-start gap-4">
          <div className="grid w-full max-w-xl gap-1.5">
            <Label>Intrinsic DateTimeInput</Label>
            <DeciDateTimeInput
              dateOnly
              serialize={false}
              value={deciDateValue}
              onValueChange={setDeciDateValue}
            />
          </div>
          <div className="grid w-full max-w-xl gap-1.5">
            <Label>Full-width DateTimeInput</Label>
            <DeciDateTimeInput
              dateOnly
              serialize={false}
              value={deciDateValue}
              onValueChange={setDeciDateValue}
              fullWidth
            />
          </div>
          <div className="grid w-full max-w-xl gap-1.5">
            <Label>Intrinsic TimeInput</Label>
            <DeciTimeInput
              value={deciTimeValue}
              onValueChange={setDeciTimeValue}
            />
          </div>
          <div className="grid w-full max-w-xl gap-1.5">
            <Label>Full-width TimeInput</Label>
            <DeciTimeInput
              value={deciTimeValue}
              onValueChange={setDeciTimeValue}
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Deci UI Fields"
        description="RHF-bound field wrappers for the new deci UI date and time inputs."
      >
        <ComponentPreview className="items-stretch justify-start">
          <Form
            className="grid w-full max-w-xl gap-4"
            defaultValues={{
              dateOnly: "2026-05-05",
              dateString: "2026-05-05",
              dateObject: new Date(),
              dateTime: "2026-05-05 14:45",
              time: "14:45",
            }}
          >
            <DeciDateTimeField
              name="dateOnly"
              label="Date only string"
              dateOnly
              helperText="Default DateTimeField storage is yyyy-mm-dd."
              fullWidth
            />
            <DeciDateTimeField
              name="dateString"
              label="Date string with explicit serializer"
              dateOnly
              serialize={dateStringSerialize}
              helperText="Stored in the form as yyyy-mm-dd through DateTimeInput serialize."
              fullWidth
            />
            <DeciDateTimeField
              name="dateObject"
              label="Date object"
              dateOnly
              serialize={false}
              helperText="serialize=false keeps Date objects in the form."
              fullWidth
            />
            <DeciDateTimeField
              name="dateTime"
              label="Date and time"
              fullWidth
            />
            <DeciTimeField
              name="time"
              label="Time"
              fullWidth
            />
          </Form>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Interview Designer Date Picker"
        description="Direct DateTimeField usage with attribute interpolation and the now shortcut."
      >
        <ComponentPreview className="items-stretch justify-start">
          <div className="w-full max-w-xl">
            <DeciDateTimeField
              name="interview-design-date-picker"
              label="Default date"
              value={interviewDateValue}
              inputValue={interviewDateValue}
              onInputValueChange={setInterviewDateValue}
              dateOnly
              allowNow
              allowArbitrary
              now={interviewDateValue === "now"}
              onNowChange={(nextNow) => setInterviewDateValue(nextNow ? "now" : "")}
              helperText="Supports now, yyyy-mm-dd, or attribute interpolation."
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Native Date Input"
        description="Plain browser date input currently used for simple filtering."
      >
        <ComponentPreview className="items-stretch justify-start">
          <div className="grid w-full max-w-sm gap-1.5">
            <Label htmlFor="native-date-input">Native date</Label>
            <TextInput
              id="native-date-input"
              type="date"
              value={nativeDateValue}
              onValueChange={(value) => setNativeDateValue(value ?? "")}
              fullWidth
            />
            <code className="text-xs text-muted-foreground">{nativeDateValue}</code>
          </div>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
